import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Box,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { NfSchema } from "./validations";
import { useForm } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import { MuiFileInput } from "mui-file-input";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { bitrixApi } from "../../services/bitrixApi";
import { companyOriginValues, companyValues, labelValues, serviceTypeValues } from "./bitrixValues";
import CurrencyTextField from "@lupus-ai/mui-currency-textfield";

export const Pj = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [file, setFile] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [nfSent, setNfSent] = useState([]);
  const [fileRenameError, setFileRenameError] = useState(false);
  const [suggestedName, setSuggestedName] = useState("");

  const partner = queryParams?.get?.("parceiro") || "";
  const company = queryParams?.get?.("empresa") || "";
  const companyOrigin = queryParams?.get?.("origem") || "";
  const serviceType = queryParams?.get?.("prestacao") || "";
  const serviceValue = queryParams?.get?.("valor") || "";
  const cnpj = queryParams?.get?.("cnpj") || "";

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
    clearErrors,
    setError,
  } = useForm({
    resolver: zodResolver(NfSchema),
    defaultValues: {
      partner,
      company,
      companyOrigin,
      serviceType,
      serviceValue,
    },
  });

  const systemsOs = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("windows")) {
      return {
        link: "https://www.youtube.com/watch?v=aUgB1ZlaNLA",
        label: "Windows",
        icon: "/portal/microsoft.png",
      };
    }

    return {
      link: "https://www.youtube.com/watch?v=y1cCUQm9wdg",
      label: "MacOS",
      icon: "/portal/apple.png",
    };
  }, []);

  const handleChange = useCallback(
    (newFile) => {
      if (newFile instanceof File && newFile.size > 0) {
        const errorMessage = "Por favor, envie um arquivo PDF.";
        const nameWithoutExt = newFile?.name?.replace?.(/\.[^/.]+$/, "");

        if (newFile.type !== "application/pdf") {
          setToast({
            open: true,
            message: errorMessage,
            severity: "error",
          });

          setError("nf", {
            type: "manual",
            message: errorMessage,
          });
          return;
        }

        if (
          !/^[a-zA-Z0-9]+$/?.test?.(nameWithoutExt) ||
          nameWithoutExt?.length > 50 ||
          nameWithoutExt?.length < 1 ||
          !nameWithoutExt
        ) {
          setFileRenameError(true);
          setSuggestedName(
            nameWithoutExt?.replace(/[^a-zA-Z0-9]/g, "")?.substring(0, 50) || "notafiscal",
          );

          setToast({
            open: true,
            message:
              "O nome do arquivo deve conter apenas letras e números (sem espaços ou símbolos), ter no mínimo 1 e no máximo 50 caracteres.",
            severity: "error",
          });

          setError("nf", {
            type: "manual",
            message:
              "O nome do arquivo deve conter apenas letras e números, entre 1 e 50 caracteres.",
          });
          return;
        }

        setToast({
          open: false,
          message: "",
          severity: "success",
        });
        setSuggestedName("");
        setFileRenameError(false);
        setFile(newFile);
        clearErrors("nf");
        setValue("nf", newFile);
      }
    },
    [clearErrors, setError, setValue],
  );

  const handleSaveServiceValue = useCallback(
    (value) => {
      clearErrors("serviceValue");
      setValue("serviceValue", value);
    },
    [clearErrors, setValue],
  );

  const handleSendToBitrix = useCallback(
    async (data) => {
      let numlead = null;

      try {
        setToast({
          open: false,
          message: "",
          severity: "success",
        });

        if (nfSent.some((item) => JSON.stringify(item) === JSON.stringify(data))) {
          setToast({
            open: true,
            message: "NF já enviada.",
            severity: "error",
          });

          return;
        }

        const cnpjField = "UF_CRM_1677495523";
        const partnerField = "UF_CRM_1677585629";
        const searchField = cnpj ? cnpjField : partnerField;
        const serachValue = cnpj ? cnpj?.trim() : data?.partner?.trim();

        const { data: bitrixPartnerResponse } = await bitrixApi.post(
          `/rest/21225/1koaurywmfxbz268/crm.company.list.json?FILTER[${searchField}]=${serachValue}`,
        );
        const partner = bitrixPartnerResponse?.result?.[0];

        if (!partner) {
          setToast({
            open: true,
            message: "Parceiro não encontrado no Bitrix24.",
            severity: "error",
          });

          return;
        }

        const values = {
          UF_CRM_1715877583: `Parceiro\n${data.partner}\n\nEmpresa\n${
            labelValues[data.company]
          }\n\nEmpresa Origem\n${
            labelValues[data.companyOrigin]
          }\n\nNF PAG - Tipo de Prestação\n${labelValues[data.serviceType]}\n`,
          UF_CRM_1733750497: companyValues[data.company],
          UF_CRM_1733750858: companyOriginValues[data.companyOrigin],
          UF_CRM_1715882834: serviceTypeValues[data.serviceType],
          UF_CRM_1728057787: `${data.serviceValue}|BRL`,
          UF_CRM_1715876719: data.partner,
          CATEGORY_ID: "83",
          STAGE_ID: "C83:NEW",
          TITLE: `${data.partner} - ${partner.TITLE}`,
        };

        const { data: createCardResponse } = await bitrixApi.post(
          `/rest/21225/1koaurywmfxbz268/crm.deal.add.json`,
          {
            fields: values,
          },
        );

        numlead = createCardResponse?.result;

        await bitrixApi.post(`/rest/21225/1koaurywmfxbz268/crm.deal.update?id=${numlead}`, {
          fields: {
            COMPANY_ID: partner.ID,
            TITLE: values.TITLE,
          },
        });

        await new Promise((resolve, reject) => {
          const fileReader = new FileReader();
          fileReader.onload = async () => {
            try {
              const base64File = fileReader.result.split(",")[1];

              const { data: bitrixSendFileResponse } = await bitrixApi.post(
                `/rest/21225/1koaurywmfxbz268/crm.deal.update.json?id=${numlead}`,
                {
                  fields: {
                    UF_CRM_1715882760: {
                      fileData: [file.name || "nota.pdf", base64File],
                    },
                  },
                }
              );

              if (!bitrixSendFileResponse?.result) {
                throw new Error("Failed to send file to Bitrix24");
              }

              resolve();
            } catch (error) {
              reject(error);
            }
          };
          fileReader.onerror = () => reject(new Error("Failed to read file"));
          fileReader.readAsDataURL(file);
        });

        await bitrixApi?.post(
          `/rest/21225/1koaurywmfxbz268/crm.deal.productrows.set?id=${numlead}&ROWS[0][PRODUCT_ID]=11221&ROWS[0][PRICE]=${data.serviceValue}&ROWS[0][QUANTITY]=1`,
        );

        const { data: dealResponse } = await bitrixApi?.post(
          `/rest/21225/1koaurywmfxbz268/crm.deal.get.json`,
          {
            ID: numlead,
          }
        );

        if (!dealResponse?.result?.UF_CRM_1715882760) {
          throw new Error("Failed to save file");
        }

        setToast({
          open: true,
          message: "NF enviada com sucesso!",
          severity: "success",
        });

        setNfSent((prev) => [...prev, data]);
      } catch (error) {
        if (numlead) {
          await bitrixApi.post(`/rest/21225/1koaurywmfxbz268/crm.deal.delete.json?id=${numlead}`);
        }

        setToast({
          open: true,
          message: "Não foi possível enviar a NF. Por favor, tente novamente.",
          severity: "error",
        });
      }
    },
    [cnpj, file, nfSent],
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
      }}
    >
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          minWidth: "100vw",
          minHeight: "100vh",
          objectFit: "cover",
          zIndex: -1,
        }}
      >
        <source src="/portal/bg-video-white.mp4" type="video/mp4" />
      </video>

      <Box
        component="img"
        src="/portal/logoPj.png"
        alt="Portal do cliente - Envio de nf"
        paddingTop={5}
        maxWidth={365}
        sx={{
          objectFit: "contain",
          width: { xs: "100%", sm: 300, md: 365 },
          maxWidth: 365,
          height: "auto",
          display: "block",
          position: "absolute",
        }}
      />

      <Snackbar
        open={toast.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={10000}
      >
        <Alert severity={toast.severity} variant="standard" sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Container
        sx={{
          padding: "8rem 0",
          display: "grid",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(handleSendToBitrix)}
          sx={{
            padding: {
              xs: "2rem",
              sm: "4rem",
            },
          }}
        >
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel htmlFor="codParc">Código do Parceiro</InputLabel>
            <OutlinedInput
              id="codParc"
              label="Código do Parceiro"
              size="small"
              type="number"
              disabled
              {...register("partner")}
            />

            {errors.partner && <FormHelperText error>{errors.partner?.message}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 2 }}>
            <InputLabel id="company">Empresa</InputLabel>
            <Select
              id="company"
              label="Empresa"
              {...register("company")}
              defaultValue={company || ""}
            >
              <MenuItem value="advance">ADVANCE VISION</MenuItem>
              <MenuItem value="aeskins">AESKINS</MenuItem>
              <MenuItem value="medbank">MEDBANK</MenuItem>
              <MenuItem value="medsystems">MEDSYSTEMS</MenuItem>
              <MenuItem value="mosaic">MOSAIC</MenuItem>
            </Select>

            {errors.company && <FormHelperText error>{errors.company?.message}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 2 }}>
            <InputLabel id="companyOrigin">Empresa origem</InputLabel>
            <Select
              id="companyOrigin"
              label="Empresa origem"
              {...register("companyOrigin")}
              defaultValue={companyOrigin || ""}
            >
              <MenuItem value="advance">ADVANCE VISION</MenuItem>
              <MenuItem value="aeskins">AESKINS</MenuItem>
              <MenuItem value="medbank">MEDBANK</MenuItem>
              <MenuItem value="medsystems">MEDSYSTEMS</MenuItem>
              <MenuItem value="mosaic">MOSAIC</MenuItem>
            </Select>

            {errors.companyOrigin && (
              <FormHelperText error>{errors.companyOrigin?.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 2 }}>
            <InputLabel id="serviceType">Tipo de prestação</InputLabel>
            <Select
              id="serviceType"
              label="Tipo de prestação"
              {...register("serviceType")}
              defaultValue={serviceType || ""}
            >
              <MenuItem value="mensal">Honorário fixo</MenuItem>
              <MenuItem value="premiacao">Honorário variável</MenuItem>
            </Select>

            {errors.serviceType && (
              <FormHelperText error>{errors.serviceType?.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 2 }}>
            <CurrencyTextField
              id="serviceValue"
              label="Valor Total da NF"
              size="small"
              variant="outlined"
              currencySymbol="R$"
              minimumValue="0"
              outputFormat="string"
              decimalCharacter=","
              digitGroupSeparator="."
              onChange={(event, value) => {
                handleSaveServiceValue(value);
              }}
            />

            {errors.serviceValue && (
              <FormHelperText error>{errors.serviceValue?.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 2 }}>
            <MuiFileInput
              size="small"
              variant="outlined"
              InputProps={{
                inputProps: {
                  accept: ".pdf",
                },
                startAdornment: <AttachFileIcon />,
              }}
              placeholder="Anexar NF"
              onChange={handleChange}
              value={file}
            />

            {errors.nf && <FormHelperText error>{errors.nf?.message}</FormHelperText>}
          </FormControl>

          {suggestedName && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              bgcolor="#e5f5fd"
              padding={2}
              borderRadius={3}
              marginTop={2}
            >
              <span>
                <strong>Nome sugerido para o arquivo:</strong> {suggestedName}
              </span>

              <LoadingButton
                variant="outlined"
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(suggestedName);
                  setToast({
                    open: true,
                    message: "Nome copiado para a área de transferência!",
                    severity: "success",
                  });
                }}
                padding={3}
              >
                Copiar
              </LoadingButton>
            </Box>
          )}

          {fileRenameError && (
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              bgcolor="#e5f5fd"
              padding={2}
              borderRadius={3}
              marginTop={2}
            >
              <img src={systemsOs.icon} width={30} height={30} alt="Microsoft logo" />
              <a href={systemsOs.link} target="_blank" rel="noopener noreferrer">
                Tutorial para renomear o arquivo no {systemsOs.label}
              </a>
            </Box>
          )}

          <LoadingButton
            loading={isSubmitting}
            disabled={isSubmitting}
            variant="contained"
            sx={{
              width: "100%",
              marginTop: "3rem",
            }}
            size="medium"
            type="submit"
          >
            Enviar
          </LoadingButton>
        </Box>
      </Container>
    </Box>
  );
};
